import { useCallback, useContext, useEffect, useMemo, useRef, useState, type FormEvent, type ReactElement, type RefObject } from 'react'
import { useIntl } from 'react-intl'
import type ReCAPTCHA from 'react-google-recaptcha'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useInterval from 'hooks/useInterval'
import ModalContext from 'contexts/ModalContext'
import GeoContext from 'contexts/geoContext'
import ContactContext from 'contexts/contactContext'
import config from 'constants/config'
import { formToObject } from 'lib/forms/formToObject'
import {
  checkUserExists,
  clearAuthError,
  forgotPassword,
  getUserKeyFromPhone,
  login,
  loginApple,
  register,
  registerWithOTP,
  sendOTPVerificationCode,
  sendSignUpOTPCode,
  validateOTPVerificationCode,
  validateRegistration,
  verifySignUpOTPCode,
} from 'actions/AuthActions'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { isWhitelabelBrand } from 'lib/whitelabels/whitelabels'
import { PENDING_ACTIONS, type PendingAction } from './constants'
import type { EmbeddedAccountAccessMode, EmbeddedAccountAccessView, JoinForm, LoginForm, OTPForm, PasswordLoginForm, PendingSignUpOTPData, Props } from './types'

interface EmbeddedAccountAccessHandlers {
  backToStart: () => void
  checkUser: (event: FormEvent<HTMLFormElement>) => void
  privateAppleEmailLogin: (data: App.AppleLoginData, email: string) => void
  passwordLogin: (event: FormEvent<HTMLFormElement>) => void
  forgotPassword: () => void
  sendLoginOtp: () => void
  submitLoginOtp: (event: FormEvent<HTMLFormElement>) => void
  resendLoginOtp: () => void
  join: (event: FormEvent<HTMLFormElement>) => void
  submitSignUpOtp: (event: FormEvent<HTMLFormElement>) => void
  resendSignUpOtp: () => void
  socialInteractionCapture: () => void
  showLoginPasswordOtp: () => void
  showJoin: () => void
}

interface UseChatEmbeddedAccountAccessResult {
  view: EmbeddedAccountAccessView
  countDown: number
  resendLabel: string
  processing: boolean
  error: any
  isWhitelabel: boolean
  currentRegionCode: string
  defaultContact?: App.GeoContact
  recaptchaRef: RefObject<ReCAPTCHA | null>
  handlers: EmbeddedAccountAccessHandlers
  isActionPending: (action: PendingAction) => boolean
}

export function useChatEmbeddedAccountAccess({
  isLastMessage = false,
  onAuthenticated,
}: Props): UseChatEmbeddedAccountAccessResult {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const { currentRegionCode } = useContext(GeoContext)
  const { defaultContact } = useContext(ContactContext)

  const [mode, setMode] = useState<EmbeddedAccountAccessMode>('start')
  const [userId, setUserId] = useState<string>('')
  const [applePrivateEmailData, setApplePrivateEmailData] = useState<App.AppleLoginData>()
  const [pendingSignUpOTPData, setPendingSignUpOTPData] = useState<PendingSignUpOTPData>()
  const [pendingJoinData, setPendingJoinData] = useState<JoinForm>()
  const [countDown, setCountDown] = useState<number>(30)
  const [hasRequestedLoginOtp, setHasRequestedLoginOtp] = useState(false)
  const [hasRequestedSignUpOtp, setHasRequestedSignUpOtp] = useState(false)
  const [isVerifyingSignUpOtp, setIsVerifyingSignUpOtp] = useState(false)
  const [pendingAction, setPendingAction] = useState<PendingAction>()

  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const hasInteractedRef = useRef(false)
  const hasSentLoggedInMessageRef = useRef(false)
  const isSendingLoggedInMessageRef = useRef(false)
  const emailCheckedRef = useRef<string>('')
  const isCheckingEmailRef = useRef(false)
  const hasSeenLoginOtpProcessingRef = useRef(false)

  const loggedIn = useAppSelector(selectLoggedIn)
  const processing = useAppSelector(state => state.auth.processing)
  const error = useAppSelector(state => state.auth.error)
  const actualUser = useAppSelector(state => (userId ? state.auth.users[userId] : undefined))
  const checkedEmailUser = useAppSelector(state => (
    emailCheckedRef.current ? state.auth.users[emailCheckedRef.current] : undefined
  ))
  const signUpOTPVerified = useAppSelector(state => state.auth.signUpOTPVerified)
  const registrationValidated = useAppSelector(state => state.auth.registrationValidated)
  const currentPathName = useAppSelector(state => state.router.location.pathname)
  const currentSearch = useAppSelector(state => state.router.location.search)

  const callbackPath = `${currentPathName}${currentSearch}`
  const otpSignUpEnabled = config.OTP_SIGN_UP_ENABLED
  const isWhitelabel = isWhitelabelBrand(config.BRAND)
  const user = actualUser
  const isEmailSignUp = Boolean(user?.email)

  const resetErrors = useCallback(() => {
    dispatch(clearAuthError())
  }, [dispatch])

  const openFallbackModal = useCallback((modal: ReactElement) => {
    hasInteractedRef.current = true
    showModal(modal)
  }, [showModal])

  const openExistingAccountModal = useCallback((email: string) => {
    setPendingJoinData(undefined)
    openFallbackModal(
      <AccountAccessModal
        initialMode="joinExistingAccount"
        defaultEmail={email}
      />,
    )
  }, [openFallbackModal])

  const changeMode = useCallback((nextMode: EmbeddedAccountAccessMode, nextUserId?: string) => {
    resetErrors()
    setMode(nextMode)
    if (nextUserId !== undefined) {
      setUserId(nextUserId)
    }
  }, [resetErrors])

  const handleBackToStart = useCallback(() => {
    setApplePrivateEmailData(undefined)
    setPendingJoinData(undefined)
    setPendingSignUpOTPData(undefined)
    setHasRequestedLoginOtp(false)
    setHasRequestedSignUpOtp(false)
    setIsVerifyingSignUpOtp(false)
    setCountDown(30)
    changeMode('start', '')
  }, [changeMode])

  useEffect(() => {
    resetErrors()
    return () => {
      dispatch(clearAuthError())
    }
  }, [dispatch, resetErrors])

  useEffect(() => {
    if (!processing) {
      setPendingAction(undefined)
    }
  }, [processing])

  useEffect(() => {
    if (!hasRequestedLoginOtp) {
      return
    }

    if (processing) {
      hasSeenLoginOtpProcessingRef.current = true
      return
    }

    if (!hasSeenLoginOtpProcessingRef.current) {
      return
    }

    hasSeenLoginOtpProcessingRef.current = false
    setHasRequestedLoginOtp(false)

    if (!error) {
      changeMode('loginOTP')
    }
  }, [changeMode, error, hasRequestedLoginOtp, processing])

  const sendAuthenticatedFollowUp = useCallback(async(signal?: AbortSignal) => {
    if (isSendingLoggedInMessageRef.current || hasSentLoggedInMessageRef.current) {
      return
    }

    isSendingLoggedInMessageRef.current = true

    try {
      await Promise.resolve(onAuthenticated(signal))
      hasSentLoggedInMessageRef.current = true
    } finally {
      isSendingLoggedInMessageRef.current = false
    }
  }, [onAuthenticated])

  useEffect(() => {
    if (!loggedIn || processing) {
      return
    }

    setMode('success')

    if (!hasInteractedRef.current || !isLastMessage || hasSentLoggedInMessageRef.current) {
      return
    }

    const abortController = new AbortController()
    sendAuthenticatedFollowUp(abortController.signal).catch(() => undefined)
    return () => {
      abortController.abort()
    }
  }, [isLastMessage, loggedIn, processing, sendAuthenticatedFollowUp])

  useEffect(() => {
    if (mode !== 'start' || processing || !actualUser) {
      return
    }

    if (actualUser.exists) {
      if (applePrivateEmailData) {
        dispatch(loginApple(applePrivateEmailData))
        return
      }

      changeMode(actualUser.maskedPhone ? 'loginPasswordOTP' : 'loginPassword', actualUser.id)
      return
    }

    if (applePrivateEmailData && actualUser.id?.endsWith('@privaterelay.appleid.com')) {
      openFallbackModal(
        <AccountAccessModal
          initialMode="applePrivateEmail"
          defaultEmail={actualUser.id}
          defaultAppleData={applePrivateEmailData}
          dismissable
        />,
      )
      return
    }

    changeMode('join', actualUser.id)
  }, [actualUser, applePrivateEmailData, changeMode, dispatch, mode, openFallbackModal, processing])

  useEffect(() => {
    if (
      !registrationValidated ||
      !pendingSignUpOTPData ||
      !pendingSignUpOTPData.phone ||
      !pendingSignUpOTPData.phonePrefix ||
      !hasRequestedSignUpOtp
    ) {
      return
    }

    dispatch(sendSignUpOTPCode({
      phone: pendingSignUpOTPData.phone,
      prefix: pendingSignUpOTPData.phonePrefix,
    }))
    setCountDown(30)
    setHasRequestedSignUpOtp(false)
    changeMode('joinOTP')
  }, [changeMode, dispatch, hasRequestedSignUpOtp, pendingSignUpOTPData, registrationValidated])

  useEffect(() => {
    if (!signUpOTPVerified || !pendingSignUpOTPData || !isVerifyingSignUpOtp) {
      return
    }

    setIsVerifyingSignUpOtp(false)
    dispatch(registerWithOTP(pendingSignUpOTPData))
  }, [dispatch, isVerifyingSignUpOtp, pendingSignUpOTPData, signUpOTPVerified])

  useEffect(() => {
    if (error && isVerifyingSignUpOtp) {
      setIsVerifyingSignUpOtp(false)
    }
  }, [error, isVerifyingSignUpOtp])

  useInterval(
    () => {
      setCountDown(curr => Math.max(curr - 1, 0))
    },
    1000,
    { disabled: countDown <= 0 || (mode !== 'loginOTP' && mode !== 'joinOTP') },
  )

  const handleCheckUser = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    hasInteractedRef.current = true

    const loginData = formToObject<LoginForm>(event.currentTarget)
    const nextUserId = loginData.phoneNumber ? getUserKeyFromPhone(loginData.phoneNumber) : loginData.email
    if (!nextUserId) {
      return
    }

    setUserId(nextUserId)
    setPendingAction(PENDING_ACTIONS.checkUser)
    dispatch(checkUserExists(loginData, recaptchaRef.current ?? undefined))
  }, [dispatch])

  const handlePrivateAppleEmailLogin = useCallback((data: App.AppleLoginData, email: string) => {
    hasInteractedRef.current = true
    setApplePrivateEmailData(data)
    setUserId(email)
    dispatch(checkUserExists({ email }, recaptchaRef.current ?? undefined))
  }, [dispatch])

  const handlePasswordLogin = useCallback(async(event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!user) {
      return
    }

    hasInteractedRef.current = true
    const loginData = formToObject<PasswordLoginForm>(event.currentTarget)
    const recaptchaData = await recaptchaRef.current?.executeAsync() ?? undefined
    setPendingAction(PENDING_ACTIONS.loginPassword)
    dispatch(login(user, loginData.password, recaptchaData))
  }, [dispatch, user])

  const handleForgotPassword = useCallback(() => {
    if (!user?.email) {
      return
    }

    hasInteractedRef.current = true
    dispatch(forgotPassword(user.email, callbackPath))
    openFallbackModal(
      <AccountAccessModal
        initialMode="forgotPassword"
        defaultEmail={user.email}
        callbackPath={callbackPath}
      />,
    )
  }, [callbackPath, dispatch, openFallbackModal, user])

  const handleSendLoginOtp = useCallback(() => {
    if (!user) {
      return
    }

    hasInteractedRef.current = true
    setCountDown(30)
    hasSeenLoginOtpProcessingRef.current = false
    setHasRequestedLoginOtp(true)
    setPendingAction(PENDING_ACTIONS.sendLoginOtp)
    dispatch(sendOTPVerificationCode(user))
  }, [dispatch, user])

  const handleSubmitLoginOtp = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!user) {
      return
    }

    hasInteractedRef.current = true
    const formData = formToObject<OTPForm>(event.currentTarget)
    setPendingAction(PENDING_ACTIONS.submitLoginOtp)
    dispatch(validateOTPVerificationCode(user, formData.code))
  }, [dispatch, user])

  const handleResendLoginOtp = useCallback(() => {
    if (!user) {
      return
    }

    hasInteractedRef.current = true
    setCountDown(30)
    dispatch(sendOTPVerificationCode(user))
  }, [dispatch, user])

  const registerUser = useCallback((joinData: JoinForm) => {
    const { phoneNumber, ...rest } = joinData
    const registrationData = {
      ...rest,
      ...(phoneNumber?.phone ? {
        phone: phoneNumber.phone,
        phonePrefix: phoneNumber.prefix,
      } : {}),
    }

    if (otpSignUpEnabled && (phoneNumber?.phone || user?.phoneNumber?.phone)) {
      const nextPending: PendingSignUpOTPData = {
        ...rest,
        phone: phoneNumber?.phone ?? user?.phoneNumber?.phone,
        phonePrefix: phoneNumber?.prefix ?? user?.phoneNumber?.prefix,
      }
      setPendingSignUpOTPData(nextPending)
      setHasRequestedSignUpOtp(true)
      setPendingAction(PENDING_ACTIONS.join)
      dispatch(validateRegistration(nextPending))
      return
    }

    setPendingAction(PENDING_ACTIONS.join)
    dispatch(register(registrationData, isEmailSignUp ? 'email' : 'phone'))
  }, [dispatch, isEmailSignUp, otpSignUpEnabled, user?.phoneNumber?.phone, user?.phoneNumber?.prefix])

  const handleJoin = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!user) {
      return
    }

    hasInteractedRef.current = true
    const joinData = formToObject<JoinForm>(event.currentTarget)

    if (isEmailSignUp) {
      registerUser(joinData)
      return
    }

    if (!joinData.email) {
      return
    }

    if (emailCheckedRef.current !== joinData.email || !checkedEmailUser) {
      isCheckingEmailRef.current = true
      emailCheckedRef.current = joinData.email
      setPendingJoinData(joinData)
      setPendingAction(PENDING_ACTIONS.join)
      dispatch(checkUserExists({ email: joinData.email, phoneNumber: user.phoneNumber }, recaptchaRef.current ?? undefined))
      return
    }

    if (checkedEmailUser.exists) {
      openExistingAccountModal(joinData.email)
      return
    }

    registerUser(joinData)
  }, [checkedEmailUser, dispatch, isEmailSignUp, openExistingAccountModal, registerUser, user])

  useEffect(() => {
    if (!isCheckingEmailRef.current || !emailCheckedRef.current) {
      return
    }

    if (processing || !checkedEmailUser) {
      return
    }

    isCheckingEmailRef.current = false

    if (checkedEmailUser.exists) {
      openExistingAccountModal(emailCheckedRef.current)
      return
    }

    if (pendingJoinData) {
      registerUser(pendingJoinData)
      setPendingJoinData(undefined)
    }
  }, [checkedEmailUser, openExistingAccountModal, pendingJoinData, processing, registerUser])

  const handleSubmitSignUpOtp = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!pendingSignUpOTPData?.phone || !pendingSignUpOTPData?.phonePrefix) {
      return
    }

    hasInteractedRef.current = true
    resetErrors()
    const formData = formToObject<OTPForm>(event.currentTarget)
    setIsVerifyingSignUpOtp(true)
    setPendingAction(PENDING_ACTIONS.submitSignUpOtp)
    dispatch(verifySignUpOTPCode({
      phone: pendingSignUpOTPData.phone,
      prefix: pendingSignUpOTPData.phonePrefix,
    }, formData.code))
  }, [dispatch, pendingSignUpOTPData, resetErrors])

  const handleResendSignUpOtp = useCallback(() => {
    if (!pendingSignUpOTPData?.phone || !pendingSignUpOTPData?.phonePrefix) {
      return
    }

    hasInteractedRef.current = true
    setCountDown(30)
    dispatch(sendSignUpOTPCode({
      phone: pendingSignUpOTPData.phone,
      prefix: pendingSignUpOTPData.phonePrefix,
    }))
  }, [dispatch, pendingSignUpOTPData])

  const handleSocialInteractionCapture = useCallback(() => {
    hasInteractedRef.current = true
  }, [])

  const showLoginPasswordOtp = useCallback(() => {
    changeMode('loginPasswordOTP')
  }, [changeMode])

  const showJoin = useCallback(() => {
    changeMode('join')
  }, [changeMode])

  const isActionPending = useCallback((action: PendingAction) => {
    return processing && pendingAction === action
  }, [pendingAction, processing])

  const view = useMemo<EmbeddedAccountAccessView>(() => {
    switch (mode) {
      case 'start':
        return { mode }
      case 'loginPassword':
      case 'loginPasswordOTP':
        return user ? { mode, user } : { mode: 'unavailable' }
      case 'loginOTP':
        return user ? { mode, user } : { mode: 'unavailable' }
      case 'join':
        return user ? { mode, user, isEmailSignUp } : { mode: 'unavailable' }
      case 'joinOTP':
        return pendingSignUpOTPData ? { mode, pendingSignUpOTPData } : { mode: 'unavailable' }
      case 'success':
        return { mode }
      default:
        return { mode: 'unavailable' }
    }
  }, [isEmailSignUp, mode, pendingSignUpOTPData, user])

  const resendLabel = useMemo(() => (
    countDown > 0
      ? intl.formatMessage({
          id: 'b3o8IjsVe_iJWo3Of7nJ',
          defaultMessage: 'Resend code in {countDown}s',
          description: 'Countdown label for embedded account access OTP resend link',
        }, { countDown })
      : intl.formatMessage({
          id: 'WMSxI3R0uQq0qnRhvfr_',
          defaultMessage: 'Resend code',
          description: 'Hyperlink text to resend OTP code during sign-up in the account access section',
        })
  ), [countDown, intl])

  const handlers = useMemo<EmbeddedAccountAccessHandlers>(() => ({
    backToStart: handleBackToStart,
    checkUser: handleCheckUser,
    privateAppleEmailLogin: handlePrivateAppleEmailLogin,
    passwordLogin: handlePasswordLogin,
    forgotPassword: handleForgotPassword,
    sendLoginOtp: handleSendLoginOtp,
    submitLoginOtp: handleSubmitLoginOtp,
    resendLoginOtp: handleResendLoginOtp,
    join: handleJoin,
    submitSignUpOtp: handleSubmitSignUpOtp,
    resendSignUpOtp: handleResendSignUpOtp,
    socialInteractionCapture: handleSocialInteractionCapture,
    showLoginPasswordOtp,
    showJoin,
  }), [
    handleBackToStart,
    handleCheckUser,
    handleForgotPassword,
    handleJoin,
    handlePasswordLogin,
    handlePrivateAppleEmailLogin,
    handleResendLoginOtp,
    handleResendSignUpOtp,
    handleSendLoginOtp,
    handleSocialInteractionCapture,
    handleSubmitLoginOtp,
    handleSubmitSignUpOtp,
    showJoin,
    showLoginPasswordOtp,
  ])

  return {
    view,
    countDown,
    resendLabel,
    processing,
    error,
    isWhitelabel,
    currentRegionCode,
    defaultContact,
    recaptchaRef,
    handlers,
    isActionPending,
  }
}
